/**
 * OwnerImpersonateBar
 *
 * Two affordances for the owner ⇄ client toggle:
 *
 *   1. <OwnerImpersonateBanner /> — a sticky 28px gold strip that ONLY
 *      renders when the current session was minted via owner-impersonate.
 *      Shows "Viewing as Jonathan Jackson · ← Return to advisor" with a
 *      one-click pop-back. Mount this once inside both client and
 *      advisor PortalShells.
 *
 *   2. <OpenSuiteAsClientButton clientUserId={…} /> — the button the
 *      owner clicks from the Advisor JourneyDetail header to drop
 *      straight into the client's suite without logging out.
 *
 * No new auth — uses /api/auth/owner-impersonate + /stop endpoints.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../../contexts/AuthContext";

export function OwnerImpersonateBanner() {
  const { user, checkSession } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  if (!user?.impersonated_by) return null;

  const handleStop = async () => {
    setExiting(true);
    try {
      await api.post("/auth/owner-impersonate/stop");
      await checkSession();
      navigate("/portal/advisor/journeys");
    } catch (_err) {
      setExiting(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-50 flex items-center justify-center gap-3 px-4 py-1.5 bg-[hsl(var(--accent-gold))]/95 text-[hsl(var(--foreground))] backdrop-blur-sm border-b border-[hsl(var(--accent-gold))]"
      data-testid="owner-impersonation-banner"
    >
      <Eye className="size-3.5 shrink-0" strokeWidth={1.5} />
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] truncate">
        Viewing as {user.name || user.email}
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={exiting}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--foreground))]/10 hover:bg-[hsl(var(--foreground))]/20 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50"
        data-testid="owner-impersonation-stop"
      >
        {exiting ? (
          <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
        ) : (
          <ArrowLeft className="size-3" strokeWidth={1.5} />
        )}
        Return to advisor
      </button>
    </div>
  );
}

export function OpenSuiteAsClientButton({ clientUserId, clientId, tripId, label = "Open suite as client", testid = "owner-open-suite" }) {
  const { checkSession } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canOpen = Boolean(clientUserId || clientId);

  const handleOpen = async () => {
    if (!canOpen) {
      setErr("This client has no Member Suite login yet.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = clientUserId
        ? { target_user_id: clientUserId }
        : { target_client_id: clientId };
      await api.post("/auth/owner-impersonate", payload);
      await checkSession();
      // Trip-scoped routing safety: if the advisor opened this from a
      // specific trip, land DIRECTLY on that trip's Member Suite, never on
      // the chooser. This eliminates the "first upcoming trip wins" leak
      // when a client has multiple experiences linked.
      navigate(tripId ? `/portal/client/experiences/${tripId}` : "/portal/client");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't open the suite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleOpen}
        disabled={busy || !canOpen}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))]/25 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={testid}
        title={
          canOpen
            ? "Open the client's Member Suite in this tab — your owner session is preserved."
            : "This client doesn't have a Suite login yet."
        }
      >
        {busy ? (
          <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
        ) : (
          <Eye className="size-3" strokeWidth={1.5} />
        )}
        {label}
      </button>
      {err && (
        <span
          className="font-accent italic text-[11px] text-[hsl(var(--accent-terracotta))]"
          data-testid="owner-open-suite-error"
        >
          {err}
        </span>
      )}
    </div>
  );
}
